import { useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Compass } from "lucide-react";
import { Button } from "@/components/ui";

/**
 * 404 fallback. Without this, an unmatched URL renders nothing — the
 * user sees a blank screen with no obvious way back to the app. Most
 * common cause is a deep-link to a deleted/renamed contact, list, or
 * automation.
 *
 * A URL that clearly isn't a page at all — an asset path like /client.js,
 * usually injected by a browser extension or an old bookmark — shouldn't
 * strand the receptionist on a dead end mid-shift. Those bounce straight to
 * the front desk. Anything that looks like a real page she mistyped still
 * gets the explanation, so a broken deep link is visible rather than silently
 * swallowed.
 */
const LOOKS_LIKE_AN_ASSET = /\.(js|mjs|cjs|css|map|json|ico|png|jpe?g|svg|woff2?|txt|xml)$/i;

export function NotFoundPage() {
  const { pathname } = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (LOOKS_LIKE_AN_ASSET.test(pathname)) navigate("/frontdesk", { replace: true });
  }, [pathname, navigate]);

  if (LOOKS_LIKE_AN_ASSET.test(pathname)) return null;

  return (
    <div className="flex min-h-[calc(100vh-3rem)] items-center justify-center p-8">
      <div className="max-w-sm text-center">
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-zinc-800/60 text-zinc-400">
          <Compass className="h-5 w-5" />
        </div>
        <h1 className="mt-4 text-lg font-semibold text-zinc-100">Page not found</h1>
        <p className="mt-2 text-sm text-zinc-500">
          The page you were looking for doesn't exist, or it may have been moved.
        </p>
        <div className="mt-5">
          {/* The front desk is where the day is run — "dashboard" pointed at a
              page that is no longer in the navigation. */}
          <Link to="/frontdesk">
            <Button size="sm">Back to the front desk</Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
